import { Button } from "@astryxdesign/core/Button";
import { Card } from "@astryxdesign/core/Card";
import { MetadataList, MetadataListItem } from "@astryxdesign/core/MetadataList";
import { HStack, VStack } from "@astryxdesign/core/Stack";
import { Text } from "@astryxdesign/core/Text";
import { TextInputMaybeError } from "@karrtopelka/tools-kit/TextInputMaybeError";
import { useToast } from "@astryxdesign/core/Toast";
import { ToolShell } from "@karrtopelka/tools-kit/ToolShell";
import { useEffect, useMemo, useState } from "react";

/**
 * Interpret whatever was typed as a moment in time.
 *
 * Accepts seconds, milliseconds, microseconds (heuristically, by digit count) and
 * anything `Date` can parse, so pasting a log line's timestamp usually just works.
 */
function interpret(input: string): { date: Date; how: string } | null {
  const text = input.trim();
  if (text === "") return null;

  if (/^-?\d+$/.test(text)) {
    const n = Number(text);
    if (!Number.isFinite(n)) return null;
    const digits = text.replace("-", "").length;
    // Seconds through ~2286, ms through ~samsara; the boundaries are the usual
    // 10/13/16-digit conventions rather than a value range, which stays predictable.
    if (digits <= 10) return { date: new Date(n * 1000), how: "Unix seconds" };
    if (digits <= 13) return { date: new Date(n), how: "Unix milliseconds" };
    if (digits <= 16) return { date: new Date(n / 1000), how: "Unix microseconds" };
    return { date: new Date(n / 1_000_000), how: "Unix nanoseconds" };
  }

  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return null;
  return { date: parsed, how: "Parsed date string" };
}

const pad = (n: number, width = 2) => String(n).padStart(width, "0");

/** `2026-08-16 20:15:04` in whichever zone the formatter parts describe. */
function localish(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/** "3 hours ago" / "in 2 days" — coarse but honest. */
function relative(from: Date, now: Date): string {
  const diffMs = from.getTime() - now.getTime();
  const abs = Math.abs(diffMs);
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ["year", 365 * 24 * 3600_000],
    ["month", 30 * 24 * 3600_000],
    ["day", 24 * 3600_000],
    ["hour", 3600_000],
    ["minute", 60_000],
    ["second", 1000],
  ];
  const fmt = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  for (const [unit, ms] of units) {
    if (abs >= ms || unit === "second") {
      return fmt.format(Math.round(diffMs / ms), unit);
    }
  }
  return "now";
}

const dash = "—";

export function App() {
  const [input, setInput] = useState("");
  const [now, setNow] = useState(() => new Date());
  const toast = useToast();

  // Live clock for the "right now" card; one tick a second is plenty.
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const parsed = useMemo(() => interpret(input), [input]);
  const invalid = input.trim() !== "" && parsed === null;
  const target = parsed?.date ?? null;
  const outOfRange = target !== null && Number.isNaN(target.getTime());

  const copy = (value: string, what: string) => {
    void navigator.clipboard
      .writeText(value)
      .then(() => toast({ body: `${what} copied`, uniqueID: "copy" }))
      .catch(() => toast({ body: "Copy failed", type: "error", uniqueID: "copy" }));
  };

  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const inputError =
    outOfRange
      ? "That number is outside the range of a JavaScript date."
      : invalid
        ? "Not a timestamp or a date this can parse."
        : null;

  return (
    <ToolShell active="epoch" tagline="Convert timestamps every direction">
      {/* Right now, always available — the most common reason to open this page. */}
      <Card padding={4}>
        <VStack gap={2}>
          <Text type="supporting" size="xsm" color="secondary">
            Right now
          </Text>
          <HStack gap={2} align="center" justify="between" wrap="wrap">
            <Text type="body" size="2xl" weight="bold" color="accent" hasTabularNumbers>
              {Math.floor(now.getTime() / 1000)}
            </Text>
            <HStack gap={1}>
              <Button
                variant="secondary"
                size="sm"
                label="Copy seconds"
                onClick={() => copy(String(Math.floor(now.getTime() / 1000)), "Seconds")}
              />
              <Button
                variant="secondary"
                size="sm"
                label="Copy ms"
                onClick={() => copy(String(now.getTime()), "Milliseconds")}
              />
            </HStack>
          </HStack>
          <Text type="supporting" size="xsm" color="secondary">
            {now.toISOString()} · {zone}
          </Text>
        </VStack>
      </Card>

      <VStack gap={2}>
        <TextInputMaybeError
          label="Timestamp or date"
          placeholder="1786907569, 2026-08-16T17:52:49Z, or 16 Aug 2026"
          value={input}
          onChange={setInput}
          error={inputError}
        />
        <HStack gap={1} wrap="wrap">
          <Button
            variant="secondary"
            size="sm"
            label="Use now"
            onClick={() => setInput(String(Math.floor(Date.now() / 1000)))}
          />
          <Button
            variant="ghost"
            size="sm"
            label="Clear"
            isDisabled={input === ""}
            onClick={() => setInput("")}
          />
        </HStack>
      </VStack>

      {target && !outOfRange ? (
        <VStack gap={1}>
          <HStack gap={1} align="center">
            <Text type="label" color="secondary">
              Interpreted
            </Text>
            <Text type="supporting" size="xsm" color="secondary">
              as {parsed?.how.toLowerCase()}
            </Text>
          </HStack>
          <Card padding={3}>
            <MetadataList columns="multi">
              <MetadataListItem label="Unix seconds">
                {Math.floor(target.getTime() / 1000)}
              </MetadataListItem>
              <MetadataListItem label="Unix milliseconds">{target.getTime()}</MetadataListItem>
              <MetadataListItem label="ISO 8601">{target.toISOString()}</MetadataListItem>
              <MetadataListItem label="UTC">{target.toUTCString()}</MetadataListItem>
              <MetadataListItem label={`Local (${zone})`}>{localish(target)}</MetadataListItem>
              <MetadataListItem label="Relative">{relative(target, now)}</MetadataListItem>
              <MetadataListItem label="Day of week">
                {target.toLocaleDateString(undefined, { weekday: "long" })}
              </MetadataListItem>
              <MetadataListItem label="Day of year">
                {Math.floor(
                  (target.getTime() - Date.UTC(target.getUTCFullYear(), 0, 0)) / 86_400_000,
                )}
              </MetadataListItem>
            </MetadataList>
          </Card>
        </VStack>
      ) : (
        <VStack gap={1}>
          <Text type="label" color="secondary">
            Accepted formats
          </Text>
          <Card padding={3}>
            <MetadataList>
              <MetadataListItem label="Seconds">1786907569 {dash} 10 digits</MetadataListItem>
              <MetadataListItem label="Milliseconds">
                1786907569000 {dash} 13 digits
              </MetadataListItem>
              <MetadataListItem label="Microseconds">
                1786907569000000 {dash} 16 digits
              </MetadataListItem>
              <MetadataListItem label="Date string">
                2026-08-16T17:52:49Z, 16 Aug 2026, 2026/08/16
              </MetadataListItem>
            </MetadataList>
          </Card>
        </VStack>
      )}

      <Text type="supporting" size="xsm" color="secondary">
        Everything runs in your browser. Nothing you type is uploaded, stored or logged.
      </Text>
    </ToolShell>
  );
}
